import React, { useState, useEffect } from "react";
import PrivacyPolicy from "./components/PrivacyPolicy";
import { useThemeStore } from "stores/v2/themeStore";
import { useUserSessionStore } from "stores/v2/userSessionStore";
import GoogleLoginButton from "./DeviceLogin/components/GoogleLoginButton";
import AzureLoginButton from "./DeviceLogin/components/AzureLoginButton";
import ClasslinkLoginButton from "./DeviceLogin/components/ClasslinkLoginButton";
import { Box, Divider } from "@chakra-ui/react";
import { Notice } from "@/components/Notice";
import { useNavigate, useLocation } from "react-router-dom";
import { shouldShowClasslinkLoginOption } from "js/utils/getLoginClientId";

interface LoginErrorNotice {
	provider: string;
	error: string;
}

interface LocationState {
	provider?: string;
	error?: string;
}

const Login: React.FC = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const [errorNotice, setErrorNotice] = useState<LoginErrorNotice>({
		provider: "",
		error: "",
	});
	const showClasslinkLoginOption = shouldShowClasslinkLoginOption(window.location.hostname);

	useEffect(() => {
		if (location.state) {
			setErrorNotice({
				provider: (location.state as LocationState)?.provider?.trim() || "",
				error: (location.state as LocationState)?.error?.trim() || "",
			});
		} else {
			setErrorNotice({
				provider: "",
				error: "",
			});
		}
	}, [location.state]);

	useEffect(() => {
		const unsubUserSessionStore = useUserSessionStore.subscribe(() => {
			if (useUserSessionStore.getState().isAuthenticated()) {
				navigate("/");
			}
		});

		return () => {
			unsubUserSessionStore();
		};
	}, [navigate]);

	return (
		<>
			<div id="login-main-content" data-testid="login-main-content">
				<div className="background">
					<div className="background-left" />
					<div className="background-right" />
				</div>
				<div className="flex-row-center privacy">
					<div className="login-content">
						<div className={useThemeStore.getState().theme === "smoothwall" ? "login-logo-smoothwall" : "login-logo"} />
						{useThemeStore.getState().theme === "linewize" && <h1>Classroom Manager </h1>}
						<h3>Teacher Portal</h3>
						<Box className="login-content-inner">
							<GoogleLoginButton />
							<AzureLoginButton />
							{showClasslinkLoginOption && <ClasslinkLoginButton />}
							{errorNotice.error.length === 0 && errorNotice.provider.length === 0 && (
								<>
									<Divider my={8} />
									<Box fontWeight="medium" mb={2} textAlign="center" color="neutrals.700">
										Signing in with a username and password?
									</Box>
									<Box mb={2} textAlign="center" color="neutrals.700">
										Contact your school's IT department for your unique login link.
									</Box>
								</>
							)}
						</Box>

						{(errorNotice.provider.length > 0 || errorNotice.error.length > 0) && (
							<Notice icon="notice" status="error" textAlign="left">
								{errorNotice.provider.length > 0 && (
									<>
										<Box fontWeight="bold">Login Method Not Available</Box>
										<Box mb={2}>It looks like your school has not enabled signing in with {errorNotice.provider} for your account.</Box>
										<Box fontWeight="bold" mb={2}>
											What to do next:
										</Box>
										<Box mb={2}>
											<Box fontWeight="bold">Try This First:</Box> Use the unique login link you received from your school's IT department.
										</Box>
										<Box mb={2}>
											<Box fontWeight="bold">If that doesn't work:</Box> Please contact IT support for assistance. They can verify your
											assigned login method.
										</Box>
									</>
								)}
								{errorNotice.error.length > 0 && (
									<>
										<Box fontWeight="bold">SSO login error with {errorNotice.error} for your account.</Box>
										<Box mb={2}>Please contact IT support for assistance. They can verify your assigned login method.</Box>
									</>
								)}
							</Notice>
						)}
					</div>
				</div>
			</div>
			<PrivacyPolicy />
		</>
	);
};

export default Login;
